import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Typography,
  Alert,
  InputAdornment,
} from '@mui/material'
import { Add as AddIcon, Business as BusinessIcon, Close as CloseIcon } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { gstApi } from '../../lib/api/gstApi'
import {
  customersApi,
  CreateCustomerPayload,
  CreateContactPayload,
  CustomerSource,
  CUSTOMER_SOURCES,
  DuplicateCheckResult,
  Customer,
} from '../../lib/api/customersApi'
import { orgsApi, lobsApi, Org, Lob } from '../../lib/api/tenantsApi'
import { themeColors } from '../../app/layout/theme'
import {
  EntityFormDrawer,
  EntityFormSection,
  EntityFormField,
  entityDrawerInputSx,
  EntityDrawerSelect,
  EntityDrawerInput,
} from '../shared/EntityFormDrawer'
import { FormInput } from '../ui'
import { authStore } from '../../lib/stores/authStore'
import companyLogo from '../../assests/drawerIcons/companyLogo.svg'
import { DuplicateWarningModal } from '../shared/DuplicateWarningModal'

const SOURCE_LABELS: Record<string, string> = {
  FACEBOOK: 'Facebook',
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  WALK_IN: 'Walk-in',
  PHONE_CALL: 'Phone Call',
  OTHER: 'Other',
}

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  companyName: '',
  city: '',
  source: 'OTHER' as CustomerSource,
  role: '',
  address: '',
  gst: '',
  pincode: '',
}

type CompanyOption =
  | { type: 'existing'; customer: Customer }
  | { type: 'create'; label: string }

const CREATE_NEW_ID = '__create_new__'

export interface CompanyDrawerProps {
  open: boolean
  onClose: () => void
  onCreated: () => void
  isSuperAdmin?: boolean
}

export function CompanyDrawer({ open, onClose, onCreated, isSuperAdmin }: CompanyDrawerProps) {
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  // GST Validation States
  const [gstLoading, setGstLoading] = useState(false)
  const [gstFeedback, setGstFeedback] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null)
  const [lastValidatedGst, setLastValidatedGst] = useState('')

  // SuperAdmin tenant selection
  const [orgs, setOrgs] = useState<Org[]>([])
  const [lobs, setLobs] = useState<Lob[]>([])
  const [orgId, setOrgId] = useState('')
  const [lobId, setLobId] = useState('')

  // ── Duplicate detection (used only when creating a new company) ─────────────
  const [dupResult, setDupResult] = useState<DuplicateCheckResult | null>(null)
  const [dupModalOpen, setDupModalOpen] = useState(false)
  const [dupChecking, setDupChecking] = useState(false)
  const stagedPayload = useRef<CreateCustomerPayload | null>(null)
  const lastChecked = useRef({ name: '', phone: '', email: '' })
  /** Tracks last contact-duplicate check inputs to avoid redundant calls */
  const lastContactChecked = useRef({ phone: '', email: '' })

  // ── Company name autocomplete ───────────────────────────────────────────────
  const [companyInputValue, setCompanyInputValue] = useState('')
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([])
  const [companyLoading, setCompanyLoading] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /**
   * When the user picks an existing company from the dropdown we switch into
   * "add contact" mode — the form collects contact details and submits to
   * POST /customers/:id/contacts instead of creating a new company.
   */
  const [selectedCompany, setSelectedCompany] = useState<Customer | null>(null)

  // ── Role tier (mirrors backend ITenantContext convention) ───────────────────
  // SuperAdmin : no orgId, no lobId            → free choice of org + LOB
  // OrgAdmin   : has orgId, no lobId           → org fixed to their own, free choice of LOB within it
  // Normal user: has both orgId and lobId      → both fixed, unchanged behaviour
  const currentUser  = authStore.user
  const userOrgId    = currentUser?.orgId ?? ''
  const userLobId    = currentUser?.lobId ?? ''
  const isOrgAdmin   = !isSuperAdmin && !!userOrgId && !userLobId

  // Load orgs when drawer opens (SuperAdmin only — Admin's org is already fixed)
  useEffect(() => {
    if (open && isSuperAdmin) {
      orgsApi.getAll().then(setOrgs).catch(() => { })
    }
  }, [open, isSuperAdmin])

  // Admin: auto-lock the org selection to their own org as soon as the drawer opens
  useEffect(() => {
    if (open && isOrgAdmin && userOrgId) {
      setOrgId(userOrgId)
    }
  }, [open, isOrgAdmin, userOrgId])

  // Reset GST validation states when drawer is opened/closed
  useEffect(() => {
    if (open) {
      setGstFeedback(null)
      setLastValidatedGst('')
      setGstLoading(false)
    }
  }, [open])

  useEffect(() => {
    if (!form.gst) {
      setGstFeedback(null)
      return
    }

    const trimmed = form.gst.trim().toUpperCase()

    // Clear feedback if user edits
    if (trimmed !== lastValidatedGst) {
      setGstFeedback(null)
    }

    if (trimmed.length !== 15) {
      return
    }

    // Check format
    const formatRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i
    if (!formatRegex.test(trimmed)) {
      setGstFeedback({ type: 'error', text: 'Invalid GST Number.' })
      return
    }

    if (trimmed === lastValidatedGst) {
      return
    }

    const timer = setTimeout(async () => {
      setGstLoading(true)
      setGstFeedback(null)
      try {
        const result = await gstApi.validateGst(trimmed)
        setLastValidatedGst(trimmed)
        if (result.isValid && result.status?.toLowerCase() === 'active') {
          setGstFeedback({ type: 'success', text: 'GST verified successfully.' })
          setForm(prev => {
            const next = { ...prev }
            if (result.legalName) {
              next.companyName = result.legalName
            }
            if (result.address) {
              next.address = result.address
            }
            if (result.pincode) {
              next.pincode = result.pincode
            }
            return next
          })
          if (result.legalName) {
            setCompanyInputValue(result.legalName)
          }
        } else if (result.status && result.status.toLowerCase() !== 'active') {
          let warningMsg = `GST is not active.\n\nStatus:\n${result.status}`
          if (result.cancellationDate) {
            let formattedDate = result.cancellationDate
            try {
              const dateObj = new Date(result.cancellationDate)
              if (!isNaN(dateObj.getTime())) {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}-${months[dateObj.getMonth()]}-${dateObj.getFullYear()}`
              }
            } catch (e) {
              // ignore
            }
            warningMsg += `\n\nCancellation Date:\n${formattedDate}`
          }
          setGstFeedback({ type: 'warning', text: warningMsg })
        } else {
          setGstFeedback({ type: 'error', text: 'Invalid GST Number.' })
        }
      } catch (err) {
        setGstFeedback({ type: 'error', text: 'Invalid GST Number.' })
      } finally {
        setGstLoading(false)
      }
    }, 700)

    return () => clearTimeout(timer)
  }, [form.gst, lastValidatedGst])

  useEffect(() => {
    if (!(isSuperAdmin || isOrgAdmin) || !orgId) { setLobs([]); setLobId(''); return }
    lobsApi.getAll(orgId).then(setLobs).catch(() => { })
    setLobId('')
  }, [orgId, isSuperAdmin, isOrgAdmin])

  const set = (k: string, v: string) => {
    setForm(f => ({ ...f, [k]: v }))
    let err = ''
    if (k === 'gst') {
      const val = v.trim()
      const isInvalid = val && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(val)
      err = isInvalid ? 'Invalid GST format' : ''
    } else if (k === 'pincode') {
      const val = v.trim()
      const isInvalid = val && !/^[1-9][0-9]{5}$/.test(val)
      err = isInvalid ? 'Invalid Pincode (6 digits)' : ''
    }
    setErrors(e => ({ ...e, [k]: err }))
  }

  // ── Company name search ─────────────────────────────────────────────────────
  const searchCompanies = useCallback(async (query: string) => {
    if (query.trim().length < 2) { setCompanyOptions([]); return }
    setCompanyLoading(true)
    try {
      const result = await customersApi.getAll({ companyNameSearch: query.trim(), pageSize: 8 })
      const existing: CompanyOption[] = result.items.map(c => ({ type: 'existing', customer: c }))
      setCompanyOptions([...existing, { type: 'create', label: query.trim() }])
    } catch {
      setCompanyOptions([{ type: 'create', label: query.trim() }])
    } finally {
      setCompanyLoading(false)
    }
  }, [])

  const handleCompanyInputChange = (_: React.SyntheticEvent, value: string) => {
    setCompanyInputValue(value)
    set('companyName', value)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => searchCompanies(value), 300)
  }

  const handleCompanySelect = (_: React.SyntheticEvent, value: CompanyOption | string | null) => {
    if (!value) { set('companyName', ''); setCompanyInputValue(''); return }

    if (typeof value === 'string') {
      set('companyName', value); setCompanyInputValue(value); return
    }

    if (value.type === 'create') {
      set('companyName', value.label); setCompanyInputValue(value.label); return
    }

    // Existing company selected → switch to "add contact" mode
    if (value.type === 'existing') {
      const c = value.customer
      setSelectedCompany(c)
      setCompanyInputValue(c.companyName ?? c.name)
      set('companyName', c.companyName ?? c.name)
      // Clear any errors
      setErrors({})
    }
  }

  const clearSelectedCompany = () => {
    setSelectedCompany(null)
    setCompanyInputValue('')
    set('companyName', '')
    setCompanyOptions([])
  }

  // ── Duplicate check — only relevant when creating a NEW company ─────────────
  const runDuplicateCheck = async (overrides?: Partial<typeof form>) => {
    if (selectedCompany) return  // not applicable in contact-add mode
    const merged = { ...form, ...overrides }
    const name = (merged.companyName || merged.name).trim()
    const phone = merged.phone.trim()
    const email = merged.email.trim()
    if (!name && !phone) return
    if (
      name === lastChecked.current.name &&
      phone === lastChecked.current.phone &&
      email === lastChecked.current.email
    ) return
    lastChecked.current = { name, phone, email }
    setDupChecking(true)
    try {
      const result = await customersApi.checkDuplicate({
        name: name || undefined,
        phone: phone || undefined,
        email: email || undefined,
      })
      setDupResult(result)
      if (result.hasExactMatch) {
        const newErrors: Record<string, string> = {}
        for (const match of result.exactMatches) {
          if (match.matchReason === 'ExactName' && name) newErrors.name = 'A company with this name already exists.'
          if (match.matchReason === 'ExactPhone' && phone) newErrors.phone = 'A company with this phone already exists.'
          if (match.matchReason === 'ExactEmail' && email) newErrors.email = 'A company with this email already exists.'
        }
        setErrors(e => ({ ...e, ...newErrors }))
      }
    } catch { /* non-blocking */ } finally { setDupChecking(false) }
  }

  // ── Contact duplicate check (phone + email) — runs on blur in contact mode ──
  const runContactDuplicateCheck = async (field: 'phone' | 'email') => {
    if (!selectedCompany) return
    const phone = form.phone.trim()
    const email = form.email.trim()

    // Need at least the field being blurred to have a value
    if (field === 'phone' && !phone) return
    if (field === 'email' && !email) return

    // Skip if nothing changed
    if (
      phone === lastContactChecked.current.phone &&
      email === lastContactChecked.current.email
    ) return

    lastContactChecked.current = { phone, email }
    setDupChecking(true)
    try {
      const result = await customersApi.checkContactDuplicate({
        phone: phone || undefined,
        email: email || undefined,
      })
      const newErrors: Record<string, string> = {}
      if (result.phoneExists && result.phoneMessage) newErrors.phone = result.phoneMessage
      if (result.emailExists && result.emailMessage) newErrors.email = result.emailMessage
      if (Object.keys(newErrors).length) setErrors(e => ({ ...e, ...newErrors }))
    } catch { /* non-blocking */ } finally { setDupChecking(false) }
  }

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.phone.trim()) e.phone = 'Phone is required'
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email'
    if (form.gst && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(form.gst.trim())) e.gst = 'Invalid GST format'
    if (form.pincode && !/^[1-9][0-9]{5}$/.test(form.pincode.trim())) e.pincode = 'Invalid Pincode (6 digits)'
    // Tenant fields only needed when creating a NEW company as SuperAdmin or Org Admin
    if (!selectedCompany && isSuperAdmin) {
      if (!orgId) e.orgId = 'Organisation is required'
      if (!lobId) e.lobId = 'Line of Business is required'
    }
    if (!selectedCompany && isOrgAdmin) {
      if (!lobId) e.lobId = 'Line of Business is required'
    }
    return e
  }

  const handleClose = () => {
    setForm({ ...EMPTY_FORM })
    setErrors({})
    setOrgId(''); setLobId('')
    setLobs([])
    setDupResult(null); setDupModalOpen(false)
    stagedPayload.current = null
    lastChecked.current = { name: '', phone: '', email: '' }
    lastContactChecked.current = { phone: '', email: '' }
    setCompanyInputValue(''); setCompanyOptions([])
    setSelectedCompany(null)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    onClose()
  }

  // ── Submit: add contact to existing company ─────────────────────────────────
  const doAddContact = async () => {
    if (!selectedCompany) return
    setSaving(true)
    try {
      const payload: CreateContactPayload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        ...(form.email && { email: form.email.trim() }),
        ...(form.role && { role: form.role.trim() }),
      }
      await customersApi.addContact(selectedCompany.id, payload)
      toast.success(`Contact added to ${selectedCompany.companyName ?? selectedCompany.name}`)
      handleClose()
      onCreated()
    } catch (err: any) {
      const msg: string = err?.response?.data?.message ?? 'Failed to add contact'
      const status: number = err?.response?.status
      if (status === 409) {
        const lower = msg.toLowerCase()
        if (lower.includes('phone')) setErrors(e => ({ ...e, phone: msg }))
        else if (lower.includes('email')) setErrors(e => ({ ...e, email: msg }))
        else toast.error(msg)
      } else {
        toast.error(msg)
      }
    } finally {
      setSaving(false)
    }
  }

  // ── Submit: create new company ──────────────────────────────────────────────
  const doCreate = async (payload: CreateCustomerPayload) => {
    setSaving(true)
    try {
      await customersApi.create(payload)
      toast.success('Company created')
      handleClose()
      onCreated()
    } catch (err: any) {
      const msg: string = err?.response?.data?.message ?? 'Failed to create company'
      const status: number = err?.response?.status
      if (status === 409) {
        const lower = msg.toLowerCase()
        if (lower.includes('phone')) setErrors(e => ({ ...e, phone: msg }))
        else if (lower.includes('name')) setErrors(e => ({ ...e, name: msg }))
        else toast.error(msg)
        await runDuplicateCheck()
      } else {
        toast.error(msg)
      }
    } finally {
      setSaving(false)
    }
  }

  const submit = async () => {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }

    // ── Mode A: add contact to existing company ──
    if (selectedCompany) {
      await doAddContact()
      return
    }

    // ── Mode B: create new company (with duplicate guard) ──
    const payload: CreateCustomerPayload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      ...(form.email && { email: form.email.trim() }),
      ...(form.companyName && { companyName: form.companyName.trim() }),
      ...(form.city && { city: form.city.trim() }),
      ...(form.address && { address: form.address.trim() }),
      ...(form.gst && { gst: form.gst.trim().toUpperCase() }),
      ...(form.pincode && { pincode: form.pincode.trim() }),
      source: form.source,
      ...(isSuperAdmin ? { orgId, lobId, warehouseId: null } : {}),
      ...(isOrgAdmin ? { lobId, warehouseId: null } : {}),
    }

    setDupChecking(true)
    let freshResult: DuplicateCheckResult | null = null
    try {
      freshResult = await customersApi.checkDuplicate({
        name: payload.name || undefined,
        phone: payload.phone || undefined,
        email: payload.email || undefined,
      })
      setDupResult(freshResult)
    } catch { /* proceed — backend catches hard duplicates */ } finally { setDupChecking(false) }

    if (freshResult?.hasExactMatch) { setDupModalOpen(true); return }
    if (freshResult?.hasFuzzyMatches) { stagedPayload.current = payload; setDupModalOpen(true); return }

    await doCreate(payload)
  }

  const handleCreateAnyway = async () => {
    setDupModalOpen(false)
    if (stagedPayload.current) await doCreate(stagedPayload.current)
  }

  const selectPlaceholder = (text: string) => (
    <Typography sx={{ color: '#8c8c8c', fontSize: 13 }}>{text}</Typography>
  )

  const ctxOrgName = authStore.user?.orgName ?? null
  const ctxLobName = authStore.user?.lobName ?? null

  const isContactMode = !!selectedCompany

  return (
    <>
      <EntityFormDrawer
        open={open}
        title={isContactMode ? 'New Contact' : 'New Company'}
        icon={<img src={companyLogo} alt="company" style={{ width: 20, height: 20 }} />}
        onClose={handleClose}
        onSubmit={submit}
        submitLabel={isContactMode ? 'Add Contact' : 'Create Company'}
        isSubmitting={saving || dupChecking}
      >
        <EntityFormSection columns={1}>

          {/* ── Company Name — live-search autocomplete ── */}
          <EntityFormField label="Company Name">
            {isContactMode ? (
              /* Locked pill when a company is already selected */
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 0.875,
                border: '1px solid #e5e7eb', borderRadius: 1,
                backgroundColor: '#f9fafb',
              }}>
                <BusinessIcon sx={{ fontSize: 15, color: '#6b7280', flexShrink: 0 }} />
                <Typography fontSize={13} fontWeight={500} sx={{ flex: 1, color: '#111827' }}>
                  {selectedCompany.companyName ?? selectedCompany.name}
                </Typography>
                <Chip
                  label="Existing"
                  size="small"
                  sx={{
                    height: 18, fontSize: '10px', fontWeight: 600,
                    backgroundColor: '#dbeafe', color: '#1d4ed8',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
                <CloseIcon
                  onClick={clearSelectedCompany}
                  sx={{ fontSize: 15, color: '#9ca3af', cursor: 'pointer', '&:hover': { color: '#ef4444' } }}
                />
              </Box>
            ) : (
              <Autocomplete
                freeSolo
                fullWidth
                size="small"
                options={companyOptions}
                inputValue={companyInputValue}
                loading={companyLoading}
                filterOptions={x => x}
                getOptionLabel={opt => {
                  if (typeof opt === 'string') return opt
                  if (opt.type === 'create') return opt.label
                  return opt.customer.companyName ?? opt.customer.name
                }}
                isOptionEqualToValue={(opt, val) => {
                  if (typeof opt === 'string' || typeof val === 'string') return false
                  if (opt.type === 'create' || (val as any).type === 'create') return false
                  return opt.customer.id === (val as any).customer?.id
                }}
                onInputChange={handleCompanyInputChange}
                onChange={handleCompanySelect}
                renderInput={params => (
                  <FormInput
                    {...params}
                    placeholder="Search or enter company name"
                    sx={entityDrawerInputSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {companyLoading
                            ? <CircularProgress color="inherit" size={14} sx={{ mr: 0.5 }} />
                            : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const key = typeof option === 'string'
                    ? option
                    : option.type === 'create'
                      ? CREATE_NEW_ID
                      : option.customer.id

                  if (typeof option === 'string') return <li {...props} key={key}>{option}</li>

                  if (option.type === 'create') {
                    return (
                      <li {...props} key={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                          <AddIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                          <Typography fontSize={13} color="#7c3aed" fontWeight={500}>
                            Create "{option.label}"
                          </Typography>
                        </Box>
                      </li>
                    )
                  }

                  const c = option.customer
                  return (
                    <li {...props} key={key}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.25, width: '100%' }}>
                        <BusinessIcon sx={{ fontSize: 15, color: '#6b7280', mt: '2px', flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography fontSize={13} fontWeight={500} noWrap>
                              {c.companyName ?? c.name}
                            </Typography>
                            <Chip label="Exists" size="small" sx={{
                              height: 16, fontSize: '10px', fontWeight: 600,
                              backgroundColor: '#fee2e2', color: '#dc2626',
                              '& .MuiChip-label': { px: 0.75 },
                            }} />
                          </Box>
                          {(c.phone || c.city) && (
                            <Typography fontSize={11} color="text.secondary" noWrap>
                              {[c.phone, c.city].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </li>
                  )
                }}
                noOptionsText={
                  companyInputValue.trim().length >= 2
                    ? 'No companies found'
                    : 'Type to search companies'
                }
                sx={{ '& .MuiOutlinedInput-root': { pr: '9px !important' } }}
              />
            )}
          </EntityFormField>

          {/* Info banner in contact mode */}
          {isContactMode && (
            <Alert
              severity="info"
              icon={false}
              sx={{
                py: 0.75, px: 1.5, fontSize: 12.5,
                backgroundColor: '#eff6ff',
                color: '#1e40af',
                border: '1px solid #bfdbfe',
                borderRadius: 1,
                '& .MuiAlert-message': { p: 0 },
              }}
            >
              Filling contact details for <strong>{selectedCompany.companyName ?? selectedCompany.name}</strong>.
              The person you add will be linked to this company.
            </Alert>
          )}
        </EntityFormSection>

        <EntityFormSection columns={2}>
            <EntityDrawerInput
              label={isContactMode ? 'Contact Name' : 'Full Name'}
              required
              value={form.name}
              onChange={v => set('name', v)}
              onBlur={() => !isContactMode && form.name.trim() && runDuplicateCheck()}
              placeholder={isContactMode ? 'e.g. Rahul Sharma' : 'e.g. Acme Corp'}
              error={errors.name}
            />

            <EntityDrawerInput
              label="Phone"
              required
              fieldType="phone"
              value={form.phone}
              onChange={v => set('phone', v)}
              onBlur={() => form.phone.trim() && runDuplicateCheck()}
              placeholder="e.g. 9876543210"
              error={errors.phone}
            />

            <EntityDrawerInput
              label="Email"
              fullWidth
              value={form.email}
              onChange={v => set('email', v)}
              onBlur={() => {
                if (isContactMode) runContactDuplicateCheck('email')
                else if (form.email.trim()) runDuplicateCheck()
              }}
              placeholder="contact@company.com"
              error={errors.email}
            />

            {/* Role — only shown when adding a contact to an existing company */}
            {isContactMode && (
              <EntityDrawerInput
                label="Role"
                fullWidth
                value={form.role}
                onChange={v => set('role', v)}
                placeholder="e.g. CEO, Manager, Sales"
              />
            )}

            {/* City and Source only shown when creating a new company */}
            {!isContactMode && (
              <>
                <EntityDrawerInput
                  label="City"
                  fullWidth
                  value={form.city}
                  onChange={v => set('city', v)}
                  placeholder="e.g. Mumbai"
                />

                <EntityDrawerSelect
                  label="Source"
                  value={form.source}
                  onChange={v => set('source', v)}
                  options={CUSTOMER_SOURCES.map(s => ({ label: SOURCE_LABELS[s], value: s }))}
                />

                <EntityDrawerInput
                  label="Address"
                  fullWidth
                  value={form.address}
                  onChange={v => set('address', v)}
                  placeholder="e.g. 123 Main St"
                />

                <EntityDrawerInput
                  label="GST Number"
                  fieldType="gstin"
                  fullWidth
                  value={form.gst}
                  onChange={v => set('gst', v)}
                  placeholder="e.g. 27AADCB2230M1Z2"
                  error={gstFeedback?.type === 'error' ? gstFeedback.text : errors.gst}
                  helperText={
                    gstFeedback?.type === 'success' || gstFeedback?.type === 'warning'
                      ? gstFeedback.text
                      : undefined
                  }
                  InputProps={{
                    endAdornment: gstLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      color: gstFeedback?.type === 'success'
                        ? '#16a34a !important'
                        : gstFeedback?.type === 'warning'
                        ? '#ea580c !important'
                        : undefined,
                      fontWeight: gstFeedback ? 600 : undefined,
                      whiteSpace: 'pre-wrap',
                    }
                  }}
                />

                <EntityDrawerInput
                  label="Pincode"
                  fieldType="pincode"
                  fullWidth
                  value={form.pincode}
                  onChange={v => set('pincode', v)}
                  placeholder="e.g. 400001"
                  error={errors.pincode}
                />
              </>
            )}
          </EntityFormSection>

          {/* Tenant Assignment — only when creating a new company */}
          {!isContactMode && (
            <Box sx={{ mt: 0.5 }}>
              <Divider sx={{ my: 1.5 }}>
                <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, px: 1 }}>
                  Tenant Assignment
                </Typography>
              </Divider>

              {isSuperAdmin ? (
                <EntityFormSection columns={1}>
                  <EntityDrawerSelect
                    label="Organisation"
                    required
                    value={orgId}
                    onChange={v => { setOrgId(v); setErrors(er => ({ ...er, orgId: '' })) }}
                    options={orgs.map(o => ({ label: o.name, value: o.id }))}
                    placeholder="Select an organisation"
                    error={errors.orgId}
                  />

                  <EntityDrawerSelect
                    label="Line of Business"
                    required
                    value={lobId}
                    onChange={v => { setLobId(v); setErrors(er => ({ ...er, lobId: '' })) }}
                    options={lobs.map(l => ({ label: l.name, value: l.id }))}
                    placeholder={orgId ? 'Select a line of business' : 'Select organisation first'}
                    disabled={!orgId}
                    error={errors.lobId}
                  />
                </EntityFormSection>
              ) : isOrgAdmin ? (
                <EntityFormSection columns={1}>
                  {/* Organisation is fixed to the Admin's own org — read-only */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f3f4f6' }}>
                    <Typography fontSize="12.5px" color="text.secondary">Organisation</Typography>
                    <Typography fontSize="12.5px" fontWeight={600} color={ctxOrgName ? 'text.primary' : '#9ca3af'}>
                      {ctxOrgName ?? '—'}
                    </Typography>
                  </Box>

                  <EntityDrawerSelect
                    label="Line of Business"
                    required
                    value={lobId}
                    onChange={v => { setLobId(v); setErrors(er => ({ ...er, lobId: '' })) }}
                    options={lobs.map(l => ({ label: l.name, value: l.id }))}
                    placeholder="Select a line of business"
                    error={errors.lobId}
                  />
                </EntityFormSection>
              ) : (
                <Box sx={{ px: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    { label: 'Organisation', value: ctxOrgName },
                    { label: 'Line of Business', value: ctxLobName },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f3f4f6' }}>
                      <Typography fontSize="12.5px" color="text.secondary">{label}</Typography>
                      <Typography fontSize="12.5px" fontWeight={600} color={value ? 'text.primary' : '#9ca3af'}>
                        {value ?? '—'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
      </EntityFormDrawer>

      {/* Duplicate detection modal — only used in new-company mode */}
      <DuplicateWarningModal
        open={dupModalOpen}
        result={dupResult}
        isChecking={dupChecking}
        onClose={() => setDupModalOpen(false)}
        onCreateAnyway={handleCreateAnyway}
      />
    </>
  )
}

